import Dropdown from "@shared/components/Dropdown/Dropdown";
import Button, { ButtonSize } from "@shared/components/ui/Button";
import theme from "@shared/utils/themeColors";
import EmojiPicker from "emoji-picker-react";
import GifPicker, { ContentFilter } from "gif-picker-react";
import React, { useEffect, useRef, useState } from "react";
import { useSupportMedia } from "../hooks/useSupportMedia";
import SupportMediaPreview from "./SupportMediaPreview";

interface SupportMessageInputProps {
  onSendMessage: (
    content: string,
    type: "image" | "video" | "gif" | "audio" | "file" | "text",
    mediaUrl?: string
  ) => void;
  disabled?: boolean;
  updateTypingStatus: (isTyping: boolean) => void;
}

const SupportMessageInput: React.FC<SupportMessageInputProps> = ({
  onSendMessage,
  disabled,
  updateTypingStatus,
}) => {
  const [message, setMessage] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const emojiRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const {
    handleFileSelect,
    handleGifSelect,
    mediaPreview,
    setMediaPreview,
    removeMedia,
  } = useSupportMedia();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const emojiPicker = document.querySelector(".EmojiPickerReact");
      const gifPicker = document.querySelector(".GifPickerReact");

      if (emojiPicker && !emojiPicker.contains(target)) {
        setShowEmojiPicker(false);
      }

      if (gifPicker && !gifPicker.contains(target)) {
        setShowGifPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);

      // Limpiar el timeout y asegurarse de que el estado de escritura sea false al desmontar
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      updateTypingStatus(false);
    };
  }, [updateTypingStatus]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    handleSendMessage();
  };

  const handleSendMessage = async () => {
    if (mediaPreview) {
      if (
        (!mediaPreview.error && mediaPreview.progress === 100) ||
        mediaPreview.type === "gif"
      ) {
        onSendMessage(
          message || mediaPreview.message || "",
          mediaPreview.type,
          mediaPreview.url
        );
        removeMedia();
        setMessage("");
        updateTypingStatus(false);
      }
    } else if (message.trim()) {
      onSendMessage(message.trim(), "text");
      setMessage("");
      updateTypingStatus(false);
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
  };

  const handleMediaUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      await handleFileSelect(file);
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };

  const handleGifSelection = async (gif: { url: string }) => {
    handleGifSelect(gif.url, message);
    setShowGifPicker(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setMessage(value);

    if (value.length > 0) {
      updateTypingStatus(true);

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      typingTimeoutRef.current = setTimeout(() => {
        updateTypingStatus(false);
      }, 1000);
    } else {
      updateTypingStatus(false);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const myItems = [
    {
      id: "multimedia",
      label: "Send Image/Video",
      onClick: () => fileInputRef.current?.click(),
    },
    {
      id: "gif",
      label: "Send GIF",
      onClick: () => setShowGifPicker(true),
    },
  ];

  return (
    <div className="sticky bottom-0">
      {/* Media Preview */}
      {mediaPreview && (
        <div
          className={`bg-white dark:bg-gray-900 border-t ${theme.border.primary} px-4 py-2 flex items-center`}
        >
          <SupportMediaPreview
            media={mediaPreview}
            onRemove={() => removeMedia()}
          />
        </div>
      )}

      <div
        className={`flex items-center justify-between bg-white dark:bg-gray-900 border-t ${theme.border.primary} px-4 sm:px-6 md:px-5 h-16 rounded-lg`}
      >
        {/* Plus button */}
        <Dropdown
          horizontalAlign="left"
          verticalAlign="top"
          title="Add"
          items={myItems}
          trigger={
            <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
              <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12C23.98 5.38 18.62.02 12 0zm6 13h-5v5h-2v-5H6v-2h5V6h2v5h5v2z" />
            </svg>
          }
          className="mr-4"
        />

        {/* File input (hidden) */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleMediaUpload}
          accept="image/*, video/*"
          className="hidden"
        />

        {/* Message input form */}
        <form className="grow flex" onSubmit={handleSubmit}>
          <div className="grow mr-3 relative">
            <label htmlFor="support-message-input" className="sr-only">
              Type a message
            </label>
            <textarea
              id="support-message-input"
              className={`form-input w-full ${theme.bg.inactive} border-transparent dark:border-transparent focus:bg-white dark:focus:bg-gray-800 placeholder-gray-500 h-[38px] mt-1`}
              autoComplete="off"
              value={message}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Type your message..."
              disabled={disabled}
            />
            {/* Emoji Button */}
            <div className="absolute inset-0 left-auto flex items-center">
              <button
                className="inline-flex items-center text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-200"
                type="button"
                onClick={() => {
                  setShowEmojiPicker(!showEmojiPicker);
                  setShowGifPicker(false);
                }}
                disabled={disabled}
              >
                <svg
                  className={`mr-2 ${theme.bg.fill}`}
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M9.793 10.002a.5.5 0 0 1 .353.853l-1.792 1.793a.5.5 0 0 1-.708 0l-1.792-1.793a.5.5 0 0 1 .353-.853h3.586Zm5.014-4.63c1.178 2.497 1.833 5.647.258 7.928-1.238 1.793-3.615 2.702-7.065 2.702S2.173 15.092.935 13.3c-1.575-2.28-.92-5.431.258-7.927A2.962 2.962 0 0 1 0 3.002a3 3 0 0 1 3-3c.787 0 1.496.309 2.029.806a5.866 5.866 0 0 1 5.942 0A2.96 2.96 0 0 1 13 .002a3 3 0 0 1 3 3c0 .974-.472 1.827-1.193 2.37Zm-1.387 6.79c1.05-1.522.417-3.835-.055-5.078C12.915 5.89 11.192 2.002 8 2.002s-4.914 3.89-5.365 5.082c-.472 1.243-1.106 3.556-.055 5.079.843 1.22 2.666 1.839 5.42 1.839s4.577-.62 5.42-1.84ZM6.67 6.62c.113.443.102.68-.433 1.442-.535.761-1.06 1.297-1.658 1.297-.597 0-1.08-.772-1.07-1.483.01-.71.916-2.306 1.997-2.306.784 0 1.05.607 1.163 1.05Zm3.824-1.05c1.08 0 1.987 1.596 1.997 2.306.01.71-.473 1.483-1.07 1.483-.598 0-1.123-.536-1.658-1.297-.535-.762-.546-1-.432-1.442.113-.443.38-1.05 1.163-1.05Z" />
                </svg>
              </button>
            </div>
          </div>

          <Button
            type="submit"
            size={ButtonSize.SMALL}
            disabled={
              (!message.trim() && !mediaPreview) ||
              disabled ||
              (mediaPreview?.progress !== undefined &&
                mediaPreview.progress < 100)
            }
          >
            Send
          </Button>
        </form>

        {/* Emoji Picker */}
        {showEmojiPicker && (
          <div className="absolute bottom-16 right-20 z-50">
            <EmojiPicker
              height={450}
              width={350}
              onEmojiClick={(emojiData) => {
                setMessage((prev) => prev + emojiData.emoji);
                setShowEmojiPicker(false);
              }}
            />
          </div>
        )}

        {/* GIF Picker */}
        {showGifPicker && (
          <div className="absolute bottom-20 left-4 z-10">
            <GifPicker
              tenorApiKey={import.meta.env.VITE_TENOR_API_KEY}
              contentFilter={ContentFilter.MEDIUM}
              width={350}
              onGifClick={handleGifSelection}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportMessageInput;
